import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import BeforeAfter from "@/components/BeforeAfter";
import SmartVideo from "@/components/SmartVideo";
import Seo from "@/components/Seo";
import { FinalCTA } from "@/pages/Home";
import { api } from "@/lib/api";

const MetaItem = ({ label, value }) => (
  <div>
    <p className="text-[10px] font-mono tracking-[0.25em] text-fog uppercase">{label}</p>
    <p className="mt-2 text-sm text-bone">{value}</p>
  </div>
);

const CaseStudy = () => {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setProject(null);
    setNotFound(false);
    api.get(`/projects/${slug}`)
      .then((res) => {
        const data = res.data;
        const g = (data.gallery || []).filter(Boolean);
        const base = [g[0] || data.hero, g[1] || g[0] || data.hero, g[2] || g[1] || g[0] || data.hero];
        data.gallery = [...base, ...g.slice(3)];
        setProject(data);
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <main data-testid="case-study-not-found" className="bg-night min-h-screen pt-40 px-6 sm:px-12 lg:px-20">
        <h1 className="text-3xl text-bone tracking-tight">Progetto non trovato.</h1>
        <Link to="/work" data-testid="back-to-work-link" className="mt-6 inline-flex items-center gap-2 text-xs font-mono tracking-[0.2em] text-gold">
          <ArrowLeft size={14} /> TUTTI I LAVORI
        </Link>
      </main>
    );
  }

  if (!project) {
    return (
      <main data-testid="case-study-loading" className="bg-night min-h-screen pt-40 px-6 sm:px-12 lg:px-20">
        <p className="text-sm font-mono tracking-[0.25em] text-fog">CARICAMENTO…</p>
      </main>
    );
  }

  return (
    <main data-testid="case-study-page" className="bg-night">
      <section className="relative h-[70vh] min-h-[480px] flex items-end">
        <Seo
          title={`${project.title} — Case Study | Nicola Verardo`}
          description={project.excerpt}
          path={`/work/${project.slug}`}
          image={project.hero}
        />
        <img src={project.hero} alt={project.title} className="absolute inset-0 w-full h-full object-cover" fetchpriority="high" />
        <div className="absolute inset-0 bg-gradient-to-t from-night via-night/40 to-night/40" />
        <div className="relative z-10 px-6 sm:px-12 lg:px-20 pb-14 w-full">
          <Link to="/work" data-testid="back-to-work-link" className="inline-flex items-center gap-2 text-[10px] font-mono tracking-[0.25em] text-fog hover:text-gold transition-colors duration-300">
            <ArrowLeft size={12} /> TUTTI I LAVORI
          </Link>
          <h1 data-testid="case-study-title" className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-normal tracking-tight text-bone max-w-3xl">
            {project.title}
          </h1>
          <p className="mt-4 text-sm text-fog max-w-xl">{project.excerpt}</p>
        </div>
      </section>

      <section className="border-y border-line">
        <div className="px-6 sm:px-12 lg:px-20 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          <MetaItem label="Cliente" value={project.client} />
          <MetaItem label="Anno" value={project.year} />
          <MetaItem label="Area" value={project.category.toUpperCase()} />
          <MetaItem label="Servizi" value={project.services.join(" · ")} />
        </div>
      </section>

      <section className="px-6 sm:px-12 lg:px-20 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 lg:gap-20">
        <Reveal>
          <p className="text-xs font-mono uppercase tracking-[0.25em] text-gold">Contesto</p>
          <p className="mt-6 text-base text-fog leading-relaxed">{project.context}</p>
        </Reveal>
        <Reveal delay={120}>
          <p className="text-xs font-mono uppercase tracking-[0.25em] text-gold">La sfida</p>
          <p className="mt-6 text-base text-fog leading-relaxed">{project.challenge}</p>
        </Reveal>
      </section>

      <section className="px-6 sm:px-12 lg:px-20">
        <Reveal>
          <img src={project.gallery[0]} alt={`${project.title} — dettaglio`} loading="lazy" className="w-full aspect-[21/9] object-cover" />
        </Reveal>
      </section>

      <section className="px-6 sm:px-12 lg:px-20 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
        <Reveal className="lg:col-span-5">
          <p className="text-xs font-mono uppercase tracking-[0.25em] text-gold">Approccio visivo</p>
          <h2 className="mt-6 text-2xl sm:text-3xl font-normal tracking-tight text-bone leading-snug">
            Metodo, non effetti.
          </h2>
          <p className="mt-6 text-base text-fog leading-relaxed">{project.approach}</p>
        </Reveal>
        <Reveal delay={120} className="lg:col-span-7">
          <img src={project.gallery[1]} alt={`${project.title} — approccio`} loading="lazy" className="w-full aspect-[4/3] object-cover" />
        </Reveal>
      </section>

      {project.before_image && project.after_image && (
        <section data-testid="before-after-section" className="px-6 sm:px-12 lg:px-20 pb-20 lg:pb-28">
          <Reveal>
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-gold mb-3">Prima / Dopo</p>
            <p className="text-sm text-fog mb-6 max-w-md">Trascina il cursore: a sinistra lo scatto grezzo, a destra l'immagine finale.</p>
            <BeforeAfter before={project.before_image} after={project.after_image} />
          </Reveal>
        </section>
      )}

      {project.video && (
        <section data-testid="case-study-video" className="px-6 sm:px-12 lg:px-20 pb-20 lg:pb-28">
          <Reveal>
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-gold mb-6">Motion</p>
            <SmartVideo url={project.video} poster={project.gallery[2]} autoPlay={false} className="w-full aspect-video bg-surface object-cover" />
          </Reveal>
        </section>
      )}

      <section className="bg-bone text-night">
        <div className="px-6 sm:px-12 lg:px-20 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 lg:gap-20">
          <Reveal>
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-night/50">Risultato</p>
            <h2 className="mt-6 text-2xl sm:text-3xl font-normal tracking-tight leading-snug">Cosa ha prodotto il progetto.</h2>
            <p className="mt-6 text-base text-night/60 leading-relaxed">{project.outcome}</p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 gap-8 content-center">
            {project.metrics.map((metric, i) => (
              <Reveal key={metric.label} delay={i * 100}>
                <div className="border-l-2 border-gold pl-6">
                  <p className="text-3xl lg:text-4xl font-light font-mono tracking-tight text-night">{metric.value}</p>
                  <p className="mt-2 text-[10px] font-mono tracking-[0.25em] text-night/50 uppercase">{metric.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 sm:px-12 lg:px-20 py-16">
        <Reveal>
          <img src={project.gallery[2]} alt={`${project.title} — finale`} loading="lazy" className="w-full aspect-[21/9] object-cover" />
        </Reveal>
      </section>

      {project.gallery.length > 3 && (
        <section data-testid="gallery-extra-section" className="px-6 sm:px-12 lg:px-20 pb-16 grid sm:grid-cols-2 gap-4">
          {project.gallery.slice(3).map((img, i, arr) => {
            const lastAlone = arr.length % 2 === 1 && i === arr.length - 1;
            return (
              <Reveal key={img} delay={(i % 2) * 100} className={lastAlone ? "sm:col-span-2" : ""}>
                <img src={img} alt={`${project.title} — galleria ${i + 4}`} loading="lazy" className={`w-full object-cover ${lastAlone ? "aspect-[21/9]" : "aspect-[4/3]"}`} />
              </Reveal>
            );
          })}
        </section>
      )}

      <FinalCTA />
    </main>
  );
};

export default CaseStudy;
